import { ReactNode, memo, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import CardMedia from "@mui/material/CardMedia";
import { SxProps, Theme } from "@mui/material/styles";
import { Image as KonvaImage, Layer, Stage } from "react-konva";

import { Annotation } from "../../../API";
import useCreateFullAnnotationContainer from "../canvas/useCreateFullAnnotationContainer";
import useCreateImageLayer from "../canvas/useCreateImageLayer";
import { KONVA_HEIGHT } from "../canvas/utils";
import { IS3MediaFile } from "./IS3MediaFileProps";
import MediaStatus from "./MediaStatus";

type S3ImageSharedProps = IS3MediaFile & {
  fixedHeight?: string;
  fixedWidth?: number;
  width?: number;
  deviceStatus?: boolean;
  height?: number;
  sx?: SxProps<Theme>;
  loadingFallback?: ReactNode;
  setKeyframeLoaded?: (value: boolean) => void;
  onClick?: () => void;
  keyFrameMessage?: string;
  mediaStatusSx?: SxProps<Theme>;
};

export type S3ImageImageProps = S3ImageSharedProps & {
  variant?: "image";
  annotationsData?: never;
};

export type S3ImageAnnotationProps = S3ImageSharedProps & {
  variant: "annotations";
  annotationsData?: Annotation[] | null;
};

export type S3ImageProps = S3ImageImageProps | S3ImageAnnotationProps;

const parseHeightValue = (height?: string): number | undefined => {
  if (!height) {
    return undefined;
  }

  const parsed = parseInt(height, 10);

  return Number.isNaN(parsed) ? undefined : parsed;
};

const S3ImageImageVariant = ({
  fixedHeight,
  s3Key,
  width,
  height,
  sx = [],
  keyFrameMessage,
  loadingFallback,
  deviceStatus,
  setKeyframeLoaded,
  onClick,
  mediaStatusSx,
}: S3ImageImageProps): JSX.Element => {
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isImageError, setIsImageError] = useState(false);

  const isDeviceOffline = !deviceStatus;

  useEffect(() => {
    if (isImageLoaded) {
      setKeyframeLoaded?.(true);
    }
  }, [isImageLoaded]);

  const variant = isImageLoading
    ? "keyframeLoading"
    : isImageError
    ? "keyframeError"
    : isDeviceOffline
    ? "keyframeOffline"
    : "keyframeLoading";

  return (
    <>
      {!isImageLoaded && isImageError && (
        <MediaStatus
          variant={variant}
          message={isDeviceOffline ? undefined : keyFrameMessage}
          messageHeight={isDeviceOffline ? undefined : fixedHeight}
          minHeight={fixedHeight}
          loadingFallback={isDeviceOffline ? undefined : loadingFallback}
          mediaStatusSx={mediaStatusSx}
        />
      )}

      {s3Key && !isImageError && (
        <CardMedia
          onClick={onClick}
          component="img"
          image={s3Key}
          width={width}
          height={fixedHeight ? fixedHeight : height}
          sx={{
            backgroundColor: "black",
            objectFit: "contain",
            objectPosition: "center",
            ...sx,
          }}
          onLoad={() => {
            setIsImageLoaded(true);

            setIsImageLoading(false);
          }}
          onError={() => {
            setIsImageLoaded(false);

            setIsImageLoading(false);

            setIsImageError(true);
          }}
        />
      )}
    </>
  );
};

const S3ImageAnnotationVariant = ({
  fixedHeight,
  fixedWidth,
  s3Key,
  annotationsData,
  setKeyframeLoaded = () => {},
  deviceStatus,
  mediaStatusSx,
  keyFrameMessage,
  loadingFallback,
}: S3ImageAnnotationProps): JSX.Element => {
  const { image, imageRef, scaleX, scaleY, skaleFactor, loading } =
    useCreateImageLayer(s3Key, setKeyframeLoaded);

  const [isTimeoutElapsed, setIsTimeoutElapsed] = useState(false);

  const fixedHeightValue = parseHeightValue(fixedHeight);
  const baseHeight = fixedHeightValue ?? KONVA_HEIGHT;
  const safeSkaleFactor = skaleFactor || 1;
  const computedStageWidth =
    Number.isFinite(baseHeight * safeSkaleFactor) && safeSkaleFactor > 0
      ? baseHeight * safeSkaleFactor
      : baseHeight;

  const heightAdjustment =
    baseHeight && baseHeight > 0 ? KONVA_HEIGHT / baseHeight : 1;
  const overlayScaleX = (scaleX || 1) * heightAdjustment;
  const overlayScaleY = (scaleY || 1) * heightAdjustment;

  const safeAnnotations = Array.isArray(annotationsData) ? annotationsData : [];

  const { fullAnnContainerRef } = useCreateFullAnnotationContainer(
    safeAnnotations,
    overlayScaleX,
    overlayScaleY
  );

  const isDeviceOffline = deviceStatus === false;
  const showKeyFrame = Boolean(image) && !loading;

  useEffect(() => {
    if (showKeyFrame) {
      setIsTimeoutElapsed(false);

      return;
    }

    const timeoutId = setTimeout(() => {
      if (!showKeyFrame) {
        setIsTimeoutElapsed(true);
      }
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [showKeyFrame]);

  const variant = isTimeoutElapsed
    ? "keyframeError"
    : isDeviceOffline
    ? "keyframeOffline"
    : "keyframeLoading";

  const placeholderContent = isDeviceOffline ? (
    <MediaStatus
      variant="keyframeOffline"
      message={"The device is offline. It can take some time"}
      minHeight={fixedHeight}
      mediaStatusSx={mediaStatusSx}
    />
  ) : (
    <MediaStatus
      variant={variant}
      message={
        isTimeoutElapsed
          ? keyFrameMessage
          : "Please wait for the keyframe to retrieve"
      }
      messageHeight={fixedHeight}
      minHeight={fixedHeight}
      loadingFallback={isTimeoutElapsed ? undefined : loadingFallback}
      mediaStatusSx={mediaStatusSx}
    />
  );

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "black",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: showKeyFrame ? "block" : "none",
          }}
        >
          <Stage
            width={fixedWidth ?? computedStageWidth}
            height={fixedHeightValue ?? baseHeight}
            style={{
              opacity: showKeyFrame ? 1 : 0,
              transition: "opacity 0.2s ease",
            }}
          >
            <Layer>
              <KonvaImage
                ref={imageRef}
                image={image}
                x={0}
                y={0}
                width={fixedWidth ?? computedStageWidth}
                height={fixedHeightValue ?? baseHeight}
              />
            </Layer>

            <Layer ref={fullAnnContainerRef} />
          </Stage>
        </Box>

        {!showKeyFrame && (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {placeholderContent}
          </Box>
        )}
      </Box>
    </>
  );
};

const S3ImageComponent = (props: S3ImageProps): JSX.Element => {
  if (props.variant === "annotations") {
    return <S3ImageAnnotationVariant {...props} />;
  }

  return <S3ImageImageVariant {...props} />;
};

const S3Image = memo(S3ImageComponent);

export default S3Image;
